import React, { useEffect, useRef } from 'react';

import {
  PANEL_ID_COOKIE_CONSENT,
  PANEL_ID_COOKIE_SETTINGS,
  PANEL_ID_LEGAL_NOTICE
} from '@components/CookieBanner/V3/utils';
import CookieBannerPanel from '@components/CookieBanner/shared/CookieBannerPanel';
import { COOKIE_BANNER_PANEL_ACTION_VARIANT } from '@components/CookieBanner/shared/CookieBannerPanel/Action';
import {
  COOKIE_BANNER_VERSION_V3,
  createNavigationLink
} from '@components/CookieBanner/shared/utils';

interface PanelMainProps {
  navigation: unknown;
  consentGiven: boolean;
  // Translations
  title: string;
  description: string;
  consentInfoLinkText: string;
  legalInfoLinkText: string;
  allowAllCookiesButtonText: string;
  customizeCookiesButtonText: string;
  rejectCookiesButtonText: string;
  // Actions
  onAllowAll: (...args: unknown[]) => unknown;
  onDecline: (...args: unknown[]) => unknown;
}

const PanelMain = ({
  navigation,
  consentGiven,

  // Translations
  title,

  description,
  consentInfoLinkText,
  legalInfoLinkText,
  allowAllCookiesButtonText,
  customizeCookiesButtonText,
  rejectCookiesButtonText,

  // Actions
  onAllowAll,

  onDecline,
  isOnlyTechnicallyNecessary
}: PanelMainProps) => {
  const buttonRefs = useRef({});
  useEffect(() => {
    if (navigation.focusElement && buttonRefs.current[navigation.focusElement]) {
      buttonRefs.current[navigation.focusElement].focus();
    }
  }, [navigation.focusElement]);

  return (
    <CookieBannerPanel.Root>
      <CookieBannerPanel.Title>{title}</CookieBannerPanel.Title>

      <CookieBannerPanel.Description parseHTML>{description}</CookieBannerPanel.Description>

      <CookieBannerPanel.Section>
        <CookieBannerPanel.Link
          version={COOKIE_BANNER_VERSION_V3}
          onClick={createNavigationLink(navigation, PANEL_ID_LEGAL_NOTICE)}
          id={PANEL_ID_LEGAL_NOTICE}
          ref={(ref) => {
            buttonRefs.current[PANEL_ID_LEGAL_NOTICE] = ref;
          }}>
          {legalInfoLinkText}
        </CookieBannerPanel.Link>
        {consentGiven && (
          <CookieBannerPanel.Link
            version={COOKIE_BANNER_VERSION_V3}
            onClick={createNavigationLink(navigation, PANEL_ID_COOKIE_CONSENT)}
            id={PANEL_ID_COOKIE_CONSENT}
            ref={(ref) => {
              buttonRefs.current[PANEL_ID_COOKIE_CONSENT] = ref;
            }}>
            {consentInfoLinkText}
          </CookieBannerPanel.Link>
        )}
      </CookieBannerPanel.Section>

      <CookieBannerPanel.ActionArea>
        {/* Customize Cookies */}
        <CookieBannerPanel.Action
          onClick={createNavigationLink(navigation, PANEL_ID_COOKIE_SETTINGS)}
          variant={COOKIE_BANNER_PANEL_ACTION_VARIANT.text}
          id={PANEL_ID_COOKIE_SETTINGS}
          ref={(ref) => {
            buttonRefs.current[PANEL_ID_COOKIE_SETTINGS] = ref;
          }}>
          <span dangerouslySetInnerHTML={{ __html: customizeCookiesButtonText }} />
        </CookieBannerPanel.Action>

        {!consentGiven && !isOnlyTechnicallyNecessary && (
          <CookieBannerPanel.Action
            onClick={onDecline}
            variant={COOKIE_BANNER_PANEL_ACTION_VARIANT.outlined}>
            <span dangerouslySetInnerHTML={{ __html: rejectCookiesButtonText }} />
          </CookieBannerPanel.Action>
        )}
        <CookieBannerPanel.Action onClick={onAllowAll}>
          <span dangerouslySetInnerHTML={{ __html: allowAllCookiesButtonText }} />
        </CookieBannerPanel.Action>
      </CookieBannerPanel.ActionArea>
    </CookieBannerPanel.Root>
  );
};

export default PanelMain;
